import jsonp from "jsonp";
import qs from "qs";
import { Unit } from "../../types/customization/box";
import { SelectionPhoto } from "../../types/selection/selection";
import BaseSelectionProvider from "./BaseSelectionProvider";

const baseUrl = (endpoint: string) => `https://graph.instagram.com/${endpoint}`;
const getAlbumPhotosUrl = () => baseUrl(`/me/media`);
const getMultiplePhotosUrl = (id: string) => baseUrl(`/${id}/children`);

export default class InstagramSelectionProvider extends BaseSelectionProvider {
  accessToken: string;
  photos: {
    data: SelectionPhoto[];
    paging?: string;
  };

  constructor(accessToken: string) {
    super();
    this.accessToken = accessToken;
    this.photos = { data: [] };
  }

  hasAlbums(): boolean {
    return false;
  }
  hasMorePhotos(): boolean {
    return !!this.photos.paging;
  }

  refreshPhotos(): void {
    this.photos = { data: [] };
    return;
  }

  async getPhotos(): Promise<SelectionPhoto[]> {
    if (this.photos.data.length > 0) {
      return this.photos.data;
    }
    this.photos.data = await this.getDistantPhotos();
    return this.photos.data;
  }

  async getMorePhotos(): Promise<SelectionPhoto[]> {
    if (!this.hasMorePhotos()) {
      return this.photos.data;
    }
    const nextPhotos = await this.getDistantPhotos(true);
    this.photos.data = this.photos.data.concat(nextPhotos);
    return this.photos.data;
  }

  async getDistantPhotos(next = false): Promise<SelectionPhoto[]> {
    return new Promise((success) => {
      const params: {
        access_token: string;
        after?: string;
        fields: string;
        limit?: number;
      } = {
        access_token: this.accessToken,
        fields: "id,media_type,media_url",
        limit: 10,
      };
      if (next && this.hasMorePhotos()) {
        params.after = this.photos.paging;
      }

      const url = getAlbumPhotosUrl() + "?" + qs.stringify(params);
      jsonp(url, (err, response: PhotosResponse) => {
        parseMultiplePhotos(response.data, this.accessToken)
          .then((photosResponse: PhotoResponse[]) => {
            return Promise.all(
              photosResponse.map(
                async (photoResponse: PhotoResponse): Promise<SelectionPhoto> => {
                  const size = await getSizeFromImageUrl(photoResponse.media_url);
                  return instagramPhotoToSelectionPhotos(photoResponse, size);
                }
              )
            ).then((photos) => success(photos));
          })
          .catch(() => {
            // todo alert instagram hoto parsing failed
          });

        this.photos.paging =
          response.paging && response.paging.cursors ? response.paging.cursors.after : undefined;
      });
    });
  }
}

async function getCarrousselPhotos(id: string, token: string): Promise<PhotosResponse> {
  return new Promise((success) => {
    const params: { access_token: string; count?: number; fields: string } = {
      access_token: token,
      fields: "id,media_type,media_url",
    };

    const url = getMultiplePhotosUrl(id) + "?" + qs.stringify(params);
    jsonp(url, (err, response: PhotosResponse) => {
      success(response);
    });
  });
}

// Get individual photos inside a carrousel
function getCarroussel(photo: PhotoResponse, token: string): Promise<PhotoResponse[]> {
  return new Promise((resolve) => {
    if (photo.media_type === "CAROUSEL_ALBUM") {
      getCarrousselPhotos(photo.id, token)
        .then((carrousel) => {
          resolve(carrousel.data);
        })
        .catch(() => {
          // todo alert getCarrousselPhotos failed
        });
    } else {
      const photos = [];
      photos.push(photo);
      resolve(photos);
    }
  });
}

// Asynchrounously get multiple photos from instagram post
function parseMultiplePhotos(data: PhotoResponse[], token: string): Promise<PhotoResponse[]> {
  return new Promise((resolve) => {
    const individualPhotos = data.reduce(
      async (result: Promise<PhotoResponse[]>, photo: PhotoResponse): Promise<PhotoResponse[]> => {
        const previousResult = await result;
        const carrousselMedias = await getCarroussel(photo, token);
        const images = carrousselMedias.filter((media) => media.media_type !== "VIDEO");
        return Promise.resolve([...previousResult, ...images]);
      },
      Promise.resolve([])
    );
    resolve(individualPhotos);
  });
}

// Instagram Api does not return photos size
function getSizeFromImageUrl(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.addEventListener("load", function () {
      resolve({ width: this.naturalWidth, height: this.naturalHeight });
    });
    img.src = url;
  });
}

function instagramPhotoToSelectionPhotos(
  photoResponse: PhotoResponse,
  size: { width: number; height: number }
): SelectionPhoto {
  return {
    key: `instagram-${photoResponse.id}`,

    height: { unit: Unit.PX, value: size.height } as const,
    width: { unit: Unit.PX, value: size.width } as const,

    provider: "instagram",
    providerRef: photoResponse.id,

    providerPictureUrl: photoResponse.media_url,
    providerThumbnailUrl: photoResponse.media_url,
  };
}

interface PhotosResponse {
  data: PhotoResponse[];
  paging?: {
    cursors?: {
      before: string;
      after: string;
    };
  };
}

interface PhotoResponse {
  id: string;
  media_url: string;
  media_type: "IMAGE" | "CAROUSEL_ALBUM" | "VIDEO";
}
